import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Navbar from '@/components/Navbar';
import QuoteWizard from '@/components/QuoteWizard';
import TrustBadges from '@/components/TrustBadges';
import Testimonials from '@/components/Testimonials';
import FAQ from '@/components/FAQ';
import Footer from '@/components/Footer';
import StickyCallBar from '@/components/StickyCallBar';
import SEOHead from '@/components/SEOHead';
import { Slider } from '@/components/Portfolio';
import { useLang } from '@/context/LanguageContext';
import { TRADE_PAGES, UI_TEXT } from '@/data/tradeData';

export default function TradePage({ tradeKey }) {
  const { lang } = useLang();
  const trade = TRADE_PAGES[tradeKey];
  const content = trade[lang];
  const t = UI_TEXT[lang];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);

  const url = lang === 'es' ? `/es/${trade.slug.es}` : `/${trade.slug.en}`;

  // JSON-LD
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": content.h1,
    "description": content.subhead,
    "provider": { "@type": "LocalBusiness", "name": "Contractors of South Florida LLC", "telephone": "305-904-8567", "areaServed": "South Florida" },
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": content.faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })),
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <SEOHead title={`${content.h1} | Contractors of South Florida`} description={content.subhead} url={url} lang={lang} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      <Navbar minimal />

      {/* Hero with wizard above fold */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <img src={trade.heroImg} alt="" aria-hidden="true" className="w-full h-[120%] object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/40" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="uppercase text-xs tracking-[0.2em] font-bold text-[#E31937] mb-6">Contractors of South Florida</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
              className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.95] text-white mb-6">{content.h1}</motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="text-[#A3A3A3] text-base leading-relaxed mb-8 max-w-md">{content.subhead}</motion.p>
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}>
              <TrustBadges badges={content.badges} />
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="bg-[#141414] border border-white/10 p-6 sm:p-8">
            <QuoteWizard preselectedService={tradeKey} embedded />
          </motion.div>
        </div>
      </section>

      {/* Gallery */}
      {trade.gallery.length > 0 && (
        <section className="py-24 sm:py-32">
          <div className="max-w-5xl mx-auto px-6 lg:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
              <p className="uppercase text-xs tracking-[0.2em] font-bold text-[#E31937] mb-4">{t.portfolio.label}</p>
              <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-white">{t.portfolio.title}</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {trade.gallery.map((g, i) => (
                <Slider key={i} before={g.before} after={g.after} title={g[lang]} category="" index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <Testimonials items={content.testimonials} />

      {/* FAQ */}
      <FAQ items={content.faqs} />

      {/* Bottom CTA */}
      <section className="py-20 sm:py-28 border-t border-white/5">
        <div className="max-w-xl mx-auto px-6 text-center">
          <h2 className="font-display font-black text-2xl sm:text-3xl text-white mb-6">{t.tradeCta}</h2>
          <a href="tel:3059048567" className="inline-block bg-[#E31937] text-white font-bold uppercase tracking-wider px-10 py-4 hover:bg-[#FF2445] transition-colors text-sm">305-904-8567</a>
        </div>
      </section>

      <Footer minimal />
      <StickyCallBar />
    </div>
  );
}
